import { useState, useCallback, useEffect, Dispatch, SetStateAction } from 'react';
import { Sortable } from 'react-sortablejs';
import { DestinationCGType, ReorderDestinationDirectionCGType } from '@/types/graphql/root';
import { reorderDestination } from '@/utils/actions';
import { getSortedDestinationList, filterNullUndefined } from '@/utils';
import useActionHandler from '@/app/hooks/useActionHandler';

interface UseDragAndDropProps {
  destinations: DestinationCGType[];
  tripId: string;
  setDestinations: Dispatch<SetStateAction<DestinationCGType[]>>;
}

export default function useDragAndDrop({ destinations, tripId, setDestinations }: UseDragAndDropProps) {
  const [isSortableDragging, setIsSortableDragging] = useState(false);

  const { execute: executeReorderDestination, data: reorderData } = useActionHandler({
    action: reorderDestination,
    key: tripId,
  });

  useEffect(() => {
    if (reorderData) {
      setDestinations(getSortedDestinationList(filterNullUndefined(reorderData)));
    }
  }, [reorderData]);

  const onSortableDragEnd = useCallback(
    async (e: Sortable.SortableEvent) => {
      const targetDestId = e.item.getAttribute('data-dest-id');
      if (!targetDestId) return;
      const targetDay = e.to.parentElement?.getAttribute('data-day');
      if (!targetDay) return;
      const targetIndex = e.newIndex;
      if (targetIndex === undefined) return;

      const destinationsByDay = destinations.filter(
        (dest) => !dest.accommType && dest.day === +targetDay && dest.id !== targetDestId,
      );

      let targetPos: number | null = null;
      if (targetIndex === 0) {
        targetPos = destinationsByDay?.[0]?.pos ?? 1;
      } else {
        targetPos = (destinationsByDay?.[targetIndex - 1]?.pos ?? 0) + 1;
      }

      if (!tripId) return;

      setDestinations((prev) => {
        const newDests = [...prev];
        return getSortedDestinationList(
          newDests.map((dest) => {
            if (dest.id === targetDestId) {
              return {
                ...dest,
                pos: targetPos,
                day: +targetDay,
              };
            } else if (dest.day === +targetDay && dest.pos >= targetPos && !dest.accommType) {
              return {
                ...dest,
                pos: dest.pos + 1,
              };
            }
            return dest;
          }),
        );
      });

      executeReorderDestination(tripId, {
        id: targetDestId,
        day: +targetDay,
        pos: targetPos,
        direction: ReorderDestinationDirectionCGType.DownCGType,
      });
    },
    [destinations, tripId],
  );

  return {
    isSortableDragging,
    setIsSortableDragging,
    onSortableDragEnd,
  };
}
